import PropTypes from 'prop-types'
import React, { useRef, useState, useCallback, useEffect } from 'react'
import ReactDOM from 'react-dom'
import classNames from 'classnames'
import VM from 'scratch-vm'

import Box from '../box/box.jsx'
import { STAGE_DISPLAY_SIZES } from '../../lib/layout-constants.js'
import StageHeader from '../../containers/stage-header.jsx'
import Stage from '../../containers/stage.jsx'
import Loader from '../loader/loader.jsx'

import styles from './stage-wrapper.css'
import { connect } from 'react-redux'
import CodeViewer from '../codeViewer/CodeViewer.js'
import Console from '../terminals/Console.js'
import SerialMonitor from '../terminals/SerialMonitor.js'
import { setCurrentMode } from '../../reducers/script.js'

import cIcon       from './icon-c.png'
import pythonIcon  from './icon-python.png'
import consoleIcon from './icon-console.png'

const DEVICES = ['peeCee','zing','crawle','klawb','roboki','toki','poki','moki']

// All devices now support Python mode.
const NO_MICROPYTHON_DEVICES = new Set()

// Returns true if the given device (from URL ?device=) does not support Python mode.
function deviceLacksMicroPython(device) {
    if (!device) return false
    const d = device.toLowerCase()
    return NO_MICROPYTHON_DEVICES.has(d)
}

const StageWrapperComponent = function (props) {
    const { isFullScreen, menu, isRtl, isRendererSupported, loading, stageSize, vm, hideSimulateScreen } = props
    const params = new URLSearchParams(window.location.search)
    const device  = params.get('device') || null

    const clearConsoleRef = useRef(null)
    const containerRef    = useRef(null)

    const [editorFlex,   setEditorFlex]   = useState(65)
    const [panelWidth,   setPanelWidth]   = useState(34)   // vw
    const [fontSize,     setFontSize]     = useState(13)
    const [showEditor,   setShowEditor]   = useState(true)
    const [showTerminal, setShowTerminal] = useState(true)
    const [showSerial,   setShowSerial]   = useState(false)
    const [menuBarPx,   setMenuBarPx]    = useState(48)   // measured on mount

    const isCoding    = !!props.currentMode?.isCodingMode
    const isC         = isCoding && !props.currentMode?.isMicroPythonMode
    const pyDisabled  = deviceLacksMicroPython(device)

    // Auto-switch to C mode if the current device doesn't support Python.
    useEffect(() => {
        if (pyDisabled && props.currentMode?.isMicroPythonMode) {
            props.setCurrentMode({ ...props.currentMode, isMicroPythonMode: false })
        }
    }, [pyDisabled]) // eslint-disable-line react-hooks/exhaustive-deps

    // Measure the actual rendered menu bar height once so the panel sits
    // exactly below it regardless of root font-size or browser zoom.
    useEffect(() => {
        const el = document.querySelector('[class*="menu-bar_menu-bar"]')
                || document.querySelector('[class*="menuBar"]')
                || document.querySelector('nav')
        if (el) setMenuBarPx(el.getBoundingClientRect().height)
    }, [])

    // Sync panel width as a CSS variable so the blocks workspace
    // can shrink to avoid the fixed editor overlay.
    useEffect(() => {
        document.documentElement.style.setProperty(
            '--coding-panel-width',
            isCoding ? `${panelWidth}vw` : (DEVICES.includes(device) ? '54px' : '0px')
        )
        // Double rAF: first frame applies CSS, second frame lets Scratch Blocks
        // read the updated container dimensions before resizing its SVG.
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                window.dispatchEvent(new Event('resize'))
            })
        })
    }, [panelWidth, isCoding, device])

    const handelConsoleClear = () => { if (clearConsoleRef.current) clearConsoleRef.current() }

    // ── ADD 3.2: Expand into Scripting ───────────────────────────────────────
    // Stores the generated code + metadata in localStorage, then opens Plode
    // Scripting in a new tab.  Scripting reads the handoff on load, creates a
    // new project with the code already in the editor, and removes the key.
    const expandIntoScripting = useCallback(async () => {
        const code     = props.codes || ''
        const language = props.currentMode?.isMicroPythonMode ? 'python' : 'c'
        const params   = new URLSearchParams(window.location.search)
        const devParam = params.get('device')  || 'peeCee'
        const verParam = params.get('version') || '1.0.12'

        // Step 1: Release the Web Serial port so Scripting can acquire it.
        // We MUST disconnect before opening the new tab — otherwise the port
        // is still held by this tab and Scripting's connect attempt fails.
        if (props.worker) {
            await new Promise((resolve) => {
                let done = false
                const finish = () => { if (!done) { done = true; clearTimeout(t); props.worker.removeEventListener('message', h); resolve() } }
                const h = (e) => { if (e.data.type === 'disconnectComplete' || e.data.type === 'connected') finish() }
                const t = setTimeout(finish, 3000)   // safety net
                props.worker.addEventListener('message', h)
                props.worker.postMessage({ type: 'disconnectAndConfirm' })
            })
        }

        // Step 2: Build the Scripting URL.
        // Check if we are running through the proxy (e.g. port 3000 or /blockbased path)
        const isProxy =
            window.location.port === '3000' ||
            window.location.pathname.startsWith('/blockbased') ||
            window.location.pathname.includes('/blockbased');

        const scriptingBase =
            window.PLODE_SCRIPTING_URL ||
            (isProxy
                ? `${window.location.origin}/scripting`
                : window.location.hostname === 'localhost'
                    ? 'http://localhost:5173/scripting'
                    : `${window.location.origin}/scripting`);

        const payload = JSON.stringify({
            code, language, device: devParam, version: verParam,
            projectName: 'Scratch Code', timestamp: Date.now(),
        })
        const encoded = btoa(unescape(encodeURIComponent(payload)))  // UTF-8 safe base64

        // Store in sessionStorage as fallback when on same origin
        try {
            sessionStorage.setItem('scratchHandoff', payload);
        } catch {}

        const target = new URL(scriptingBase, window.location.origin)
        target.searchParams.set('device',          devParam)
        target.searchParams.set('version',         verParam)
        target.searchParams.set('language',        language)
        target.searchParams.set('microPythonMode', language === 'python' ? 'true' : 'false')
        target.searchParams.set('fromScratch',     '1')
        // Code payload lives in the hash — never sent to the server, no length limit
        target.hash = `scratchCode=${encoded}`

        // Step 3: Always open Scripting in the same tab so the Web Serial port
        // is never held open in two tabs simultaneously.
        window.location.assign(target.toString())
    }, [props.codes, props.currentMode, props.worker])

    const handleDividerMouseDown = useCallback((e) => {
        e.preventDefault()
        const move = (ev) => {
            if (!containerRef.current) return
            const rect = containerRef.current.getBoundingClientRect()
            setEditorFlex(Math.min(Math.max(((ev.clientY - rect.top) / rect.height) * 100, 15), 85))
        }
        const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up) }
        window.addEventListener('mousemove', move)
        window.addEventListener('mouseup', up)
    }, [])

    // Panel is fixed to the RIGHT edge — dragging handle left = wider
    const handleWidthDragStart = useCallback((e) => {
        e.preventDefault()
        const startX = e.clientX, startW = panelWidth
        const move = (ev) => {
            const dxVw = (startX - ev.clientX) / (window.innerWidth / 100)
            const newW = Math.min(Math.max(startW + dxVw, 18), 80)
            setPanelWidth(newW)
            // Update CSS variable instantly so blocks wrapper clips in lockstep with panel
            document.documentElement.style.setProperty('--coding-panel-width', `${newW}vw`)
        }
        const up = () => {
            window.removeEventListener('mousemove', move)
            window.removeEventListener('mouseup', up)
            // Fire resize only once when drag ends so Scratch Blocks reflows cleanly
            requestAnimationFrame(() => {
                requestAnimationFrame(() => window.dispatchEvent(new Event('resize')))
            })
        }
        window.addEventListener('mousemove', move)
        window.addEventListener('mouseup', up)
    }, [panelWidth])

    // ── CODING MODE — rendered via portal at document.body ──────────────────
    // Using a portal removes the panel from the Scratch GUI flex layout so the
    // blocks canvas expands to fill the full viewport with zero blue gap.
    if (isCoding) {
        const hasBelowPanel = showTerminal || (showSerial && isC)

        // Use the measured menu bar height so the panel sits flush below it
        // regardless of root font-size or browser zoom level.
        const codingPanel = (
            <div style={{
                position: 'fixed', right: 0,
                top: menuBarPx,
                width: `${panelWidth}vw`,
                height: `calc(100vh - ${menuBarPx}px)`,
                display: 'flex', flexDirection: 'row',
                zIndex: 300,
            }}>
                {/* Left-edge width drag handle */}
                <div className={styles.widthHandle} onMouseDown={handleWidthDragStart} title="Drag to resize">
                    <div className={styles.widthHandleGrip} />
                </div>

                <div style={{ flex:1, minWidth:0, display:'flex', flexDirection:'column', overflow:'hidden', background:'transparent' }}>
                    {/* Editor + terminal area — no top header, green flag lives in sidebar */}
                    <div className={styles.customStage} style={{ flex:1, minHeight:0, height:'auto' }}>

                        {/* PANELS */}
                        <div className={styles.panels} ref={containerRef}>
                            {showEditor && (
                                <>
                                    <div className={styles.editorPane}
                                         style={{ height: hasBelowPanel ? `${editorFlex}%` : '100%' }}>
                                        <CodeViewer fontSize={fontSize} />
                                    </div>
                                    {hasBelowPanel && (
                                        <div className={styles.resizeDivider} onMouseDown={handleDividerMouseDown} title="Drag to resize">
                                            <div className={styles.resizeDividerGrip} />
                                        </div>
                                    )}
                                </>
                            )}
                            {showTerminal && (
                                <div className={styles.consolePane} style={{ height: showEditor ? undefined : '100%' }}>
                                    <Console clearConsoleRef={clearConsoleRef} />
                                </div>
                            )}
                            {showSerial && isC && (
                                <div className={styles.consolePane} style={{ height: showEditor ? undefined : '100%' }}>
                                    <SerialMonitor />
                                </div>
                            )}
                            {!showEditor && !showTerminal && !(showSerial && isC) && (
                                <div className={styles.emptyState}><span>Use the sidebar to show panels.</span></div>
                            )}
                        </div>

                        {/* RIGHT SIDEBAR */}
                        <div className={styles.sidebar}>

                            {/* ── Green flag pinned at top ────────────── */}
                            {menu && (
                                <div className={styles.sidebarFlagWrap}>
                                    <StageHeader stageSize={stageSize} vm={vm} handelConsoleClear={handelConsoleClear} />
                                </div>
                            )}

                            {/* ── Close editor — same style/position as the open button in non-coding mode ── */}
                            <button
                                className={styles.sidebarBtn}
                                style={{ marginBottom: 4 }}
                                title="Close Code Editor"
                                onClick={() => props.setCurrentMode({ ...props.currentMode, isCodingMode: false })}
                            >
                                <svg viewBox="0 0 20 20" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="7,4 3,10 7,16"/><polyline points="13,4 17,10 13,16"/>
                                    <line x1="11" y1="3" x2="9" y2="17"/>
                                </svg>
                            </button>

                            {/* ── ADD 3.2: Expand button — directly below close button ── */}
                            <button
                                className={styles.sidebarBtn}
                                style={{ marginBottom: 4 }}
                                onClick={expandIntoScripting}
                                title="Open this code in Plode Scripting"
                            >
                                <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="10,2 14,2 14,6"/><line x1="14" y1="2" x2="9" y2="7"/>
                                    <polyline points="6,14 2,14 2,10"/><line x1="2" y1="14" x2="7" y2="9"/>
                                </svg>
                            </button>

                            {/* ── Button groups — vertically centred ──── */}
                            <div className={styles.sidebarBtnArea}>
                            <div className={styles.sidebarGroup}>
                                <button
                                    className={`${styles.sidebarBtnLang} ${!props.currentMode?.isMicroPythonMode ? styles.sidebarBtnLangActive : ''}`}
                                    title="Switch to C / Arduino"
                                    onClick={() => props.currentMode?.isMicroPythonMode && props.setCurrentMode({ ...props.currentMode, isMicroPythonMode: false })}>
                                    <img src={cIcon} alt="C" className={styles.sidebarIcon} draggable={false} />
                                </button>
                                <button
                                    className={`${styles.sidebarBtnLang} ${props.currentMode?.isMicroPythonMode ? styles.sidebarBtnLangActive : ''} ${pyDisabled ? styles.sidebarBtnDisabled : ''}`}
                                    title={pyDisabled
                                        ? `MicroPython is not supported on ${device || 'this device'}. Use C code mode instead.`
                                        : 'Switch to Python'}
                                    disabled={pyDisabled}
                                    style={pyDisabled ? { opacity: 0.35, cursor: 'not-allowed', pointerEvents: 'all' } : undefined}
                                    onClick={() => !pyDisabled && !props.currentMode?.isMicroPythonMode && props.setCurrentMode({ ...props.currentMode, isMicroPythonMode: true })}>
                                    <img src={pythonIcon} alt="Python" className={styles.sidebarIcon} draggable={false} />
                                </button>
                            </div>
                            <div className={styles.sidebarGroup}>
                                <button className={`${styles.sidebarBtn} ${showEditor ? styles.sidebarBtnView : ''}`}
                                    title="Toggle code editor" onClick={() => setShowEditor(v => !v)}>
                                    <svg viewBox="0 0 20 20" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="6,7 2,10 6,13"/><polyline points="14,7 18,10 14,13"/>
                                        <line x1="11" y1="5" x2="9" y2="15"/>
                                    </svg>
                                </button>
                                <button className={`${styles.sidebarBtn} ${showTerminal ? styles.sidebarBtnView : ''}`}
                                    title="Toggle terminal" onClick={() => setShowTerminal(v => !v)}>
                                    <img src={consoleIcon} alt="Terminal" className={styles.sidebarIcon} draggable={false} />
                                </button>
                                <button
                                    className={`${styles.sidebarBtn} ${showSerial && isC ? styles.sidebarBtnView : ''} ${!isC ? styles.sidebarBtnDisabled : ''}`}
                                    title={isC ? 'Serial monitor' : 'Serial monitor is only available in C mode.'}
                                    onClick={() => isC && setShowSerial(v => !v)}
                                    style={!isC ? { opacity: 0.3, cursor: 'not-allowed' } : {}}
                                >
                                    <svg viewBox="0 0 20 20" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="1.5" y="3" width="17" height="12" rx="1.5"/>
                                        <polyline points="3.5,12 5.5,12 5.5,8 7.5,8 7.5,12 9.5,12 9.5,7 11.5,7 11.5,12 13.5,12 13.5,9 15.5,9 15.5,12 17,12"/>
                                        <line x1="7" y1="17" x2="13" y2="17"/><line x1="10" y1="15" x2="10" y2="17"/>
                                    </svg>
                                </button>
                            </div>
                            <div className={styles.sidebarGroup}>
                                <button className={styles.sidebarBtn} title="Zoom in"  onClick={() => setFontSize(s => Math.min(s+1,24))}>
                                    <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                                        <circle cx="6.5" cy="6.5" r="4.5"/>
                                        <line x1="6.5" y1="4" x2="6.5" y2="9"/><line x1="4" y1="6.5" x2="9" y2="6.5"/>
                                        <line x1="10" y1="10" x2="13.5" y2="13.5"/>
                                    </svg>
                                </button>
                                <button className={styles.sidebarBtn} title="Zoom out" onClick={() => setFontSize(s => Math.max(s-1,9))}>
                                    <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                                        <circle cx="6.5" cy="6.5" r="4.5"/>
                                        <line x1="4" y1="6.5" x2="9" y2="6.5"/><line x1="10" y1="10" x2="13.5" y2="13.5"/>
                                    </svg>
                                </button>
                                <button className={styles.sidebarBtn} title="Reset zoom" onClick={() => setFontSize(13)}>
                                    <svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                                        <line x1="2" y1="4" x2="14" y2="4"/>
                                        <line x1="2" y1="8" x2="14" y2="8"/>
                                        <line x1="2" y1="12" x2="9" y2="12"/>
                                    </svg>
                                </button>
                            </div>
                            </div>{/* end sidebarBtnArea */}
                        </div>{/* end sidebar */}
                    </div>{/* end customStage */}
                </div>{/* end content */}
            </div>
        )

        // Render into document.body — completely outside Scratch GUI flex layout
        return ReactDOM.createPortal(codingPanel, document.body)
    }

    // ── NON-CODING MODE (normal Scratch stage) ───────────────────────────────
    return (
        <Box className={classNames(styles.stageWrapper, { [styles.fullScreen]: isFullScreen })} dir="ltr">
            {menu && !DEVICES.includes(device) && (
                <Box className={styles.stageMenuWrapper}>
                    <StageHeader stageSize={stageSize} vm={vm} handelConsoleClear={handelConsoleClear} />
                </Box>
            )}
            {/* For Plode devices: fixed sidebar at the same position as the coding panel sidebar */}
            {DEVICES.includes(device) && (
                <div style={{
                    position: 'fixed', right: 0, top: menuBarPx,
                    width: 54, display: 'flex', flexDirection: 'column',
                    alignItems: 'center', zIndex: 300,
                }}>
                    <div className={styles.sidebarFlagWrap}>
                        <StageHeader stageSize={stageSize} vm={vm} handelConsoleClear={handelConsoleClear} />
                    </div>
                    <button
                        className={styles.sidebarBtn}
                        style={{ marginBottom: 4 }}
                        onClick={() => props.setCurrentMode({ ...props.currentMode, isCodingMode: true })}
                        title="Open Code Editor"
                    >
                        <svg viewBox="0 0 20 20" width="19" height="19" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="7,4 3,10 7,16"/><polyline points="13,4 17,10 13,16"/>
                            <line x1="11" y1="3" x2="9" y2="17"/>
                        </svg>
                    </button>
                </div>
            )}
            <Box className={DEVICES.includes(device) ? styles.stageCanvasWrappers : styles.stageCanvasWrapper}>
                {isRendererSupported && (
                    <Stage stageSize={stageSize} vm={vm} id="pyCodeEditor" />
                )}
            </Box>
            {loading && <Loader isFullScreen={isFullScreen} />}
        </Box>
    )
}

StageWrapperComponent.propTypes = {
    isFullScreen: PropTypes.bool,
    menu: PropTypes.bool.isRequired,
    isRendererSupported: PropTypes.bool.isRequired,
    isRtl: PropTypes.bool.isRequired,
    loading: PropTypes.bool,
    stageSize: PropTypes.oneOf(Object.keys(STAGE_DISPLAY_SIZES)).isRequired,
    vm: PropTypes.instanceOf(VM).isRequired,
    hideSimulateScreen: PropTypes.bool,
}

const mapStateToProps = (state) => ({
    worker:      state.scratchGui.worker,
    currentMode: state.scratchGui.script.currentMode,
    codes:       state.scratchGui.script.codes,
})

const mapDispatchToProps = (dispatch) => ({
    setCurrentMode: (data) => dispatch(setCurrentMode(data)),
})

export default connect(mapStateToProps, mapDispatchToProps)(StageWrapperComponent)
